'use client';

import React, { useCallback, useId, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import IconComponent from 'ui/src/components/Icon';
import twMerge from 'utilities/src/twMerge';
import SearchDropdown, { getSearchPath } from './_SearchDropdown';
import useDismiss from './_useDismiss';
import type { NavbarData } from './types';

type Props = Pick<NavbarData, 'popularSearches' | 'recentlyUpdated' | 'searchPlaceholder'> & {
  className?: string;
  dropdownClassName?: string;
  dimPage?: boolean;
  onNavigate?: () => void;
};

export default function SearchField({
  popularSearches,
  recentlyUpdated,
  searchPlaceholder,
  className,
  dropdownClassName,
  dimPage = false,
  onNavigate,
}: Props) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  // The dropdown mounts on first focus so its links stay out of every page's HTML.
  const [hasDropdown, setHasDropdown] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownId = useId();
  const router = useRouter();

  const close = useCallback(() => setIsOpen(false), []);
  useDismiss(ref, isOpen, close);

  const open = () => {
    if (hasDropdown) {
      setIsOpen(true);
      return;
    }
    setHasDropdown(true);
    // Waits for the closed dropdown to paint, so the first open animates too.
    requestAnimationFrame(() => requestAnimationFrame(() => setIsOpen(true)));
  };

  const onResultClick = () => {
    close();
    onNavigate?.();
  };

  // Focus moving into the dropdown (a result, or the dropdown itself on click) keeps it open.
  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!ref.current?.contains(event.relatedTarget)) close();
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = query.trim();
    if (!value) return;
    onResultClick();
    router.push(getSearchPath(value));
  };

  return (
    <div ref={ref} onBlur={onBlur} className={twMerge('relative flex items-center', className)}>
      {dimPage && (
        <div
          aria-hidden="true"
          className={twMerge(
            'bg-site-neutrals-900/30 fixed inset-0 -z-10 transition-all duration-200',
            isOpen ? 'visible opacity-100' : 'invisible opacity-0',
          )}
        />
      )}
      <form role="search" onSubmit={onSubmit} className="w-full">
        <div
          className={twMerge(
            'bg-site-neutrals-0 text-site-neutrals-900 flex items-center gap-2.5 rounded border px-3.5 py-2.5',
            isOpen ? 'border-site-neutrals-900' : 'border-site-neutrals-100',
          )}
        >
          <IconComponent iconName="Search" size="IconS" className="bg-current" />
          <input
            type="search"
            aria-label="Search the blog"
            aria-controls={isOpen ? dropdownId : undefined}
            value={query}
            placeholder={searchPlaceholder}
            onChange={(event) => {
              setQuery(event.target.value);
              open();
            }}
            onFocus={open}
            className="site-paragraph-m placeholder:text-site-neutrals-400 min-w-0 flex-1 bg-transparent outline-none [&::-webkit-search-cancel-button]:hidden"
          />
        </div>
      </form>
      {hasDropdown && (
        <SearchDropdown
          id={dropdownId}
          isOpen={isOpen}
          query={query}
          popularSearches={popularSearches}
          recentlyUpdated={recentlyUpdated}
          onNavigate={onResultClick}
          className={dropdownClassName}
        />
      )}
    </div>
  );
}
